import React, { useMemo, useState } from "react";

import { INextGenDashboardQuestion } from "../../services/zeos-api/contracts/Dashboard";

interface IProps {
  /** The chart's question set from the API. */
  questions?: INextGenDashboardQuestion[];
  /** Legacy "Answers:" line, used as a single canned question when there's no question set. */
  answers?: string | null;
  /** Names the control for screen readers, e.g. the chart title. */
  label: string;
  /** Called when a canned question is picked, so the page can apply its range / compare preset. */
  onSelect?: (question: INextGenDashboardQuestion) => void;
  /** Controlled selection (question text). Lets the page keep the pick across the refetch a preset
   * triggers, which remounts the cards. Omit to let the bar track its own selection. */
  selectedText?: string | null;
}

export const MODEL_COMING_SOON = "MODEL · coming soon";
export const VOICE_COMING_SOON = "Voice questions are coming with Energy Modeling Studio";

const MicIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <rect x="9" y="2.5" width="6" height="11" rx="3" fill="#fff" stroke="none" />
    <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
    <line x1="12" y1="18" x2="12" y2="21.5" />
  </svg>
);

// Mid-fi Ask bar (UX-05). This phase ships canned questions only: static prompts that set up the
// chart, with no model call. MODEL what-ifs and the mic are shown disabled until the Energy Modeling
// Studio / Lamarr.AI integration exists (roadmap F-303 / F-304; agreed with Alan 9/30). Free-text
// "ask your own" is left out, since nothing can answer it yet.
export const AskBar = ({ questions, answers, label, onSelect, selectedText }: IProps) => {
  const options = useMemo<INextGenDashboardQuestion[]>(() => {
    if (questions?.length) {
      return questions;
    }
    return answers ? [{ text: answers, kind: "canned" }] : [];
  }, [questions, answers]);

  const firstCanned = Math.max(0, options.findIndex((q) => q.kind === "canned"));
  const [ownIndex, setOwnIndex] = useState<number>(firstCanned);

  if (!options.length) {
    return null;
  }

  const controlledIndex =
    selectedText != null ? options.findIndex((q) => q.text === selectedText) : -1;
  const selectedIndex = controlledIndex >= 0 ? controlledIndex : ownIndex;
  const selected = options[selectedIndex] ?? options[firstCanned];
  const hasChoices = options.length > 1;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const index = Number(event.target.value);
    const question = options[index];
    if (!question || question.kind !== "canned") {
      return;
    }
    setOwnIndex(index);
    onSelect?.(question);
  };

  return (
    <div className="my-2 flex max-w-[660px] items-center gap-2.5 rounded-[22px] border-[1.5px] border-mf-border bg-white py-1.5 pl-3.5 pr-1.5 shadow-mf-card">
      <span
        className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-mf-slate-soft text-[11px] font-extrabold text-mf-slate"
        aria-hidden="true"
      >
        Q
      </span>
      <div className="relative flex min-w-0 flex-1 items-center">
        <span className="min-w-0 flex-1 text-[12.5px] font-bold leading-snug text-mf-ink">
          {selected.text}
        </span>
        {hasChoices ? (
          <>
            <span className="px-1 text-[10px] text-mf-faint" aria-hidden="true">
              ▾
            </span>
            <select
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              aria-label={`Question for ${label}`}
              value={String(selectedIndex)}
              onChange={handleChange}
            >
              {options.map((question, index) => (
                <option
                  key={`${question.kind}-${question.text}`}
                  value={String(index)}
                  disabled={question.kind !== "canned"}
                >
                  {question.kind === "canned"
                    ? question.text
                    : `${question.text} (${MODEL_COMING_SOON})`}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </div>
      <button
        type="button"
        disabled
        title={VOICE_COMING_SOON}
        aria-label={VOICE_COMING_SOON}
        className="flex h-[30px] w-[30px] flex-none cursor-not-allowed items-center justify-center rounded-full bg-mf-orange opacity-50 shadow-sm"
      >
        <MicIcon />
      </button>
    </div>
  );
};
